/**
 * @typedef {Object} SelectablesService
 *
 * @property {Object} ITEMS
 * @property {number} selectablesCreatedCount
 * @property {function} createQualifications
 * @property {function} createSelectableMajors
 * @property {function} getSelectablesById
 * @property {function} updateSelectableMajors
 * @property {function} deregisterUrlSync
 */


/**
 * @typedef {Object} Selectables
 *
 * @property {SelectablesConfig} config
 * @property {SelectablesModel} model
 * @property {Object} _
 */


/**
 * @typedef {Object} SelectablesConfig
 *
 * @property {number} selectorsCount
 * @property {string} type
 * @property {{primaryItem: string, secondaryItem: string}} queryParamsId
 * @property {{primaryItem: string, secondaryItem: string}} queryParamsValues
 * @property {boolean} conditionalSelection
 */


/**
 * @typedef {Object} SelectablesModel
 *
 * @property {Object[]} all
 * @property {Object[]} others
 * @property {Object} primaryItem
 * @property {Object} prevPrimaryItem
 * @property {Object} secondaryItem
 * @property {Object} prevSecondaryItem
 * @property {Object[]} primarySelectionList
 * @property {Object[]} secondarySelectionList
 */


(function() {
    'use strict';

    angular.module( APP_ID )
        .factory( 'SelectablesService', [ '$q', '$state', '$transitions', '$uiRouterGlobals', 'LoggerFactory', 'TEXTS', 'CONFIG', SelectablesService ]);


     /**
     * Generic container for data objects that can be 'selected' by
     * the user.
     *
     * @param {ng.IQService} $q
     * @param {uirouter.State} $state
     * @param {uirouter.Transitions} $transitions
     * @param {uirouter.UiRouterGlobals} $uiRouterGlobals
     * @param {LoggerFactory} LoggerFactory
     * @param {Object} TEXTS
     * @param {Object} CONFIG
     *
     * @returns {SelectablesService}
     */
    function SelectablesService(
        $q,
        $state,
        $transitions,
        $uiRouterGlobals,
        LoggerFactory,
        TEXTS,
        CONFIG
    ){

        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( 'SelectablesService', false );


        /* ===== CONSTANTS ================================================== */

        /** @constant */
        var ITEMS = {
            ALL:                'all',
            OTHERS:             'others',

            PRIMARY:            'primaryItem',
            PREV_PRIMARY:       'prevPrimaryItem',

            SECONDARY:          'secondaryItem',
            PREV_SECONDARY:     'prevSecondaryItem',

            SELECTOR_PRIMARY:   'primarySelectionList',
            SELECTOR_SECONDARY: 'secondarySelectionList'
        },


        SELECTABLES_TYPE = {
            MAJORS: 'majors',
            QUALS:  'qualifications',
            SPECIALISATIONS: 'specialisation'
        },

        DEFAULTS = {
            SELECTORS_COUNT: 1
        },

        MAJORS = {
            LISTS:      'majorsLists',
            DISCLAIMER: 'disclaimer'
        };


        /** Members. */
        var onBeforeHookFn;


        /* ===== PRIVATE ATTRIBUTES ========================================= */


        var selectablesDefers = {};



        /* ===== PUBLIC ATTRIBUTES ========================================== */

        /** Caching object for {Selectable}s. */
        var selectables = {};


        /** @type {SelectablesService} */
        var service = {
            // Constants
            ITEMS: ITEMS,

            // Members
            selectablesCreatedCount: 0,

            // Methods
            createQualifications:   createQualifications,

            deregisterUrlSync:      deregisterUrlSync,

            createSelectableMajors: createSelectableMajors,
            getSelectablesById:     getSelectablesById,
            updateSelectableMajors: updateSelectableMajors,

            getSelectablesByIdPromise: getSelectablesByIdPromise
        };



        ////////////////////////////////////////////////////////////////////////

        return service; // Expose API

        ////////////////////////////////////////////////////////////////////////



        /* ===== PUBLIC METHODS ============================================= */


        /**
         * Tries to retrieve the Selectable dataset from the cache
         * based on the given ID. If it does not exist, returns {null}.
         *
         * @param {string} selectablesId
         *
         * @returns {(null|Selectables)}
         */
        function getSelectablesById ( selectablesId ) {
            if ( selectables.hasOwnProperty( selectablesId )){
                return selectables[ selectablesId ];
            } else {
                logger.error( "Selectables `%s` does not exist and so it cannot be retrieved.", selectablesId );
                return null;
            }
        }


        /**
         * Retrieves a {Promise} for Selectable dataset based on the
         * given ID.
         *
         * Note: Be careful - Errors about incorrect `selectablesId` are
         * not thrown. If a given ID does not exist, {Promise} will never
         * resolve.
         *
         * @param {string} selectablesId
         *
         * @returns {Promise<Selectables>}
         */
        function getSelectablesByIdPromise( selectablesId ) {
            if ( !selectablesDefers.hasOwnProperty( selectablesId ) ) {
                var defer = $q.defer();

                if ( !selectablesId ){
                    logger.error( "Given `selectablesId` is empty and so the selectables data structure cannot be retrieved.", selectablesId );
                    defer.reject();
                    return defer.promise;
                }

                selectablesDefers[ selectablesId ] = defer;

                if ( selectables.hasOwnProperty( selectablesId ) ) {
                    defer.resolve( selectables[ selectablesId ] );
                }
            }

            return selectablesDefers[ selectablesId ].promise;
        }


        /**
         * Constructs generic {Selectable} for a Major-based data and
         * adds it into service's `selectables` key-based caching Object.
         *
         * @param {string} selectablesName
         * @param {Object} majorsObject
         * @param {number} selectorsCount
         */
        function createSelectableMajors( selectablesName, majorsObject, selectorsCount ) {
            // Whenever new qual is created -> flush its old promise from the cache
            removeSelectablesPromiseIfExists( selectablesName );

            var isSelectionConditional = ( majorsObject.custom && selectorsCount > 1 );

            // Init `selectable` object

            /**
             * Init default {Selectables} for Majors data objects.
             *
             * @type {Selectables}
             */
            var selectableMajors = {

                // Configuration
                config: {
                    selectorsCount: selectorsCount || DEFAULTS.SELECTORS_COUNT,
                    type:           SELECTABLES_TYPE.MAJORS,
                    queryParamsId: {
                        primaryItem:   CONFIG.PARAMS.PRIMARY_MAJOR,
                        secondaryItem: CONFIG.PARAMS.SECONDARY_MAJOR
                    },
                    queryParamsValues: {
                        primaryItem:   $uiRouterGlobals.params[ CONFIG.PARAMS.PRIMARY_MAJOR ],
                        secondaryItem: $uiRouterGlobals.params[ CONFIG.PARAMS.SECONDARY_MAJOR ]
                    },
                    conditionalSelection: isSelectionConditional
                },

                // Model
                model: {
                    //all:                    [],
                    others:                 [],
                    primaryItem:            null,
                    prevPrimaryItem:        null,
                    secondaryItem:          null,
                    prevSecondaryItem:      null,
                    // primarySelectionList:   [],
                    // secondarySelectionList: []
                },

                // TEXTS
                '_': TEXTS.DEGREE.MAJORS
            };

            // Conditional selection => link definition list & custom warning.
            if ( selectableMajors.config.conditionalSelection ){
                selectableMajors.model[ MAJORS.LISTS ] = majorsObject.custom.majorsLists;
                selectableMajors._[ MAJORS.DISCLAIMER ] = majorsObject.custom.baMajorsDisclaimer;
            }

            // Prepare models
            prepareMajorsModel( selectableMajors, majorsObject.majors );

            // Make available to all other directives and components
            selectables[ selectablesName ]  = selectableMajors;
            service.selectablesCreatedCount++;

            // Major prepared - resolve Promise for those who wait for it
            resolveSelectablesPromiseIfExists( selectablesName );
        }


        /**
         * Constructs generic {Selectable} for a Qualification-based data and
         * adds it into service's `selectables` key-based caching Object.
         *
         * @param {string} selectablesName
         * @param {Object} qualsObject
         * @param {string} selectableAttribute
         */
        function createQualifications( selectablesName, qualsObject, selectableAttribute ) {
            // Whenever new qual is created -> flush its old promise from the cache
            removeSelectablesPromiseIfExists( selectablesName );

            /** @type {string} */
            var selectableAttributeId = selectableAttribute.toUpperCase();
            

            /** @type {string} */
            var queryId = CONFIG.PARAMS[ selectableAttributeId ];   // specialisation

            /**
             * Init default {Selectables} for Qualifications data objects.
             *
             * @type {Selectables}
             */
            var selectableQuals = {

                // Configuration
                config: {
                    selectorsCount: DEFAULTS.SELECTORS_COUNT,
                    type:           (selectableAttributeId === "SPECIALISATION" ? SELECTABLES_TYPE.SPECIALISATIONS : SELECTABLES_TYPE.QUALS),
                    queryParamsId: {
                        primaryItem:   queryId,
                        secondaryItem: null
                    },
                    queryParamsValues: {
                        primaryItem:   $uiRouterGlobals.params[ queryId ],
                        secondaryItem: null
                    },
                    selectability: selectableAttribute
                },

                // Model
                model: {
                    //all:                    [],
                    others:                 [],
                    primaryItem:            null,
                    prevPrimaryItem:        null,
                    secondaryItem:          null,
                    prevSecondaryItem:      null,
                    // primarySelectionList:   [],
                    // secondarySelectionList: []
                },

                // TEXTS
                '_': TEXTS.POSTGRAD.SELECTABLES[ selectableAttributeId ]
            };

            // Register transition hook
            if ( !onBeforeHookFn ) {
                onBeforeHookFn = $transitions.onBefore( { to: '**' }, function( $transition$ ) {
                    /** @type {uirouter.ParamsDefinition} */
                    var toParams = angular.copy( $transition$.params() );  // $transition$.params() is immutable => make a deep copy

                    var selectedQual = selectables[ selectablesName ];
                    // logger.warn("selected qual", JSON.stringify(selectedQual));

                    // model and URL do not match -> sync model based on the URL!
                    if ( ( selectedQual.model.primaryItem && selectedQual.model.primaryItem.id ) !== toParams[ selectedQual.config.queryParamsId.primaryItem ] || selectedQual.model.primaryItem == toParams[ selectedQual.config.queryParamsId.primaryItem ] ) {
                        // Invalidation
                        updateQualsFromSelectable( selectedQual, qualsObject, toParams );
                    }
                });
            }

            // Prepare Models
            prepareQualificationsModel( selectableQuals, qualsObject );

            // Make available to all other directives and components
            selectables[ selectablesName ] = selectableQuals;

            service.selectablesCreatedCount++;

            resolveSelectablesPromiseIfExists( selectablesName );
        }


        /**
         * Updates majors-based {Selectables} with the given ID
         * (if it exists).
         *
         * @type {string} selectablesName
         */
        function updateSelectableMajors( selectablesName ) {
            if ( !selectables.hasOwnProperty( selectablesName ) ){
                logger.warn( "Majors selectables with ID `%s` do not exist and so they cannot be updated.", selectablesName );
                return;
            }

            var selectableMajors = selectables[ selectablesName ];

            // Update the primary, secondary and others - This fails with specialisations
            updateSelectableStructure( selectableMajors );

            // Define selection lists
            defineMajorsSelectionLists( selectableMajors );
        }


        /**
         * Stops a synchronisation of the URL queries with currenty
         * selected items.
         *
         * Note: Should be called within the Controller's $onDestroy().
         */
        function deregisterUrlSync() {
            if ( onBeforeHookFn ) {
                onBeforeHookFn();
                onBeforeHookFn = null;
            }
        }





        /* ===== PRIVATE METHODS ============================================ */


        /**
         * Updates model of the existing {Selectables} based on
         * the source `qualsObject` and future {uirouter.ParamsDefinition}.
         *
         * Tip: Useful for updating without need to recreate the
         * entire object and lose all existing pointer references.
         *
         * @param {Selectables} selectable
         * @param {Object} qualsObject
         * @param {uirouter.ParamsDefinition} newParamsObject
         */
        function updateQualsFromSelectable( selectable, qualsObject, newParamsObject ) {
            /** @type {uirouter.ParamsDefinition} */
            newParamsObject = newParamsObject || $uiRouterGlobals.params;

            // Update URLs
            selectable.config.queryParamsValues[ ITEMS.PRIMARY ] = newParamsObject[ selectable.config.queryParamsId[ ITEMS.PRIMARY ] ]

            // Default selected items on the model
            selectable.model.primaryItem = null;

            // Update models from qualsObject
            prepareQualificationsModel( selectable, qualsObject );

            // Notify all listening directives and components
            service.selectablesCreatedCount++;
        }


        /**
         * Takes raw object with list of qualifications and transforms it into
         * a model that can be reused within more generic {Selectables}.
         *
         * @param {Object[]} selectableQuals
         * @param {Object[]} rawQuals
         */
        function prepareQualificationsModel( selectableQuals, rawQuals ) {
            var qual, qualsAll,

            queryParamsValues = selectableQuals.config.queryParamsValues,
            selectability     = selectableQuals.config.selectability,
            _                 = TEXTS.POSTGRAD.SELECTABLES[ selectability.toUpperCase() ];

        
            qualsAll = rawQuals.map( function ( rawQual ) {
                qual = {
                    id:         rawQual.metadata.id,
                    title:      rawQual.metadata.name,
                    metadata:   rawQual.metadata,
                    content:    rawQual,
                    group:      _.SELECTION_HEADLINE
                };
                

                assignTypeFromCurrentQueries( qual.id, qual, queryParamsValues );

                return qual;
            });

            // logger.warn("qualsall", JSON.stringify(qualsAll)); // prints all raw qualifications

            selectableQuals.model[ ITEMS.ALL ] = qualsAll;

            // Prepare other groups
            divideItemsToModelGroups( selectableQuals );

            // Prepare lists
            selectableQuals.model[ ITEMS.SELECTOR_PRIMARY ] = selectableQuals.model[ ITEMS.ALL ];
        }


        /**
         * Initialises URL query values within provided
         * `selectableConfig` depending on how many selector(s)
         * is/are enabled.
         *
         * @param {SelectablesConfig} selectableConfig
         */
        function validateUrlQueries( selectableConfig ) {
            if ( selectableConfig.selectorsCount < 1 ) {
                selectableConfig.queryParamsValues[ ITEMS.PRIMARY ] = null;
            }

            if ( selectableConfig.selectorsCount < 2 ) {
                selectableConfig.queryParamsValues[ ITEMS.SECONDARY ] = null;
            }
        }


        /**
         * Takes raw object with list of majors and transforms it into
         * a model that can be reused within more generic {Selectables}.
         *
         * @param {Object[]} selectableMajors
         * @param {Object[]} rawMajors
         */
        function prepareMajorsModel( selectableMajors, rawMajors ) {
            var major, majorsAll,

            queryParamsValues = selectableMajors.config.queryParamsValues,

            majorsModel  = selectableMajors.model,
            majorsConfig = selectableMajors.config;

            validateUrlQueries( majorsConfig ); // Validate queries against number of selectors

            // Prepare 'all' items group.
            majorsAll = rawMajors.map( function ( rawMajor ) {
                major = {
                    id:      rawMajor.asset_metadata_WebPath,
                    title:   rawMajor.name,
                    content: rawMajor.asset_contents
                };

                major.metadata = rawMajor.metadata;
                major.group = getMajorGroupId( major.id, selectableMajors );

                if ( !major.group ) major.group = TEXTS.DEGREE.MAJORS.SELECTOR.DEFAULT_GROUP_LABEL;

                assignTypeFromCurrentQueries( major.id, major, queryParamsValues );

                return major;
            });

            majorsModel[ ITEMS.ALL ] = majorsAll;

            // Prepare other groups
            divideItemsToModelGroups( selectableMajors );

            // Define selection lists
            defineMajorsSelectionLists( selectableMajors );
        }


        /**
         * Inspects the `selectableMajors` data model and determines
         * into what group of majors (~ `majorGroupId`) does the major
         * with specified `majorId` belong.
         *
         * @param {string} majorId
         * @param {Selectables} selectableMajors
         *
         * @returns {string}
         */
        function getMajorGroupId( majorId, selectableMajors ) {
            if ( !selectableMajors.config.conditionalSelection ) return null;

            var majorGroupId = null,
            majorsLists      = selectableMajors.model[ MAJORS.LISTS ];

            majorsLists.forEach( function( majorGroup ) {
                if ( majorGroupId !== null ) return;

                majorGroup.majors.some( function( majorToCompare ) {
                    if ( majorToCompare.asset_metadata_WebPath === majorId ) {
                        majorGroupId = majorGroup.type;
                        return true;
                    }
                });
            });

            return majorGroupId;
        }


        /**
         * Creates a list of Objects with Majors data model that can be
         * supplied to app's directives supporting selections.
         *
         * @param {Selectables} selectableMajors
         */
        function defineMajorsSelectionLists( selectableMajors ) {
            var majorsModel        = selectableMajors.model,
            isSelectionConditional = selectableMajors.config.conditionalSelection;

            majorsModel[ ITEMS.SELECTOR_PRIMARY ] = majorsModel[ ITEMS.SELECTOR_SECONDARY ] = majorsModel[ ITEMS.ALL ];

            if ( isSelectionConditional && majorsModel[ ITEMS.PRIMARY ] ){
                fillSelectionListsByBACondition( selectableMajors );
            }
        }


        /**
         * Creates a list of Objects with Majors data model based on
         * very niche restrictions and rules that apply to
         * Bachelor of Arts majors ONLY.
         *
         * As a result, some of the BA-based majors can be ommited from
         * the list if the do not meet pre-defined (and hard-coded) conditions.
         *
         * @param {Selectables} selectableMajors
         */
        function fillSelectionListsByBACondition( selectableMajors ) {
            // Need at least 2 lists, skipping...
            if ( selectableMajors.model[ MAJORS.LISTS ].length !== 2 ) return;

            var majorsLists = selectableMajors.model[ MAJORS.LISTS ],

            majorsAll       = selectableMajors.model[ ITEMS.ALL ],

            primaryMajor    = selectableMajors.model[ ITEMS.PRIMARY ],
            secondaryMajor  = selectableMajors.model[ ITEMS.SECONDARY ],

            majorsListA     = majorsLists[ 0 ].majors,
            majorsListB     = majorsLists[ 1 ].majors;

            // Is primary major from Part B? -> Apply conditions and fill in the selector lists.
            if ( isMajorInMajorsList( primaryMajor.id, majorsListB ) ) {
                selectableMajors.model[ ITEMS.SELECTOR_SECONDARY ] = [];

                majorsAll.forEach( function( majorToCompare ){
                    if ( primaryMajor && isMajorInMajorsList( majorToCompare.id, majorsListA ) ) {
                        selectableMajors.model[ ITEMS.SELECTOR_SECONDARY ].push( majorToCompare );
                    }
                });
            } else if ( secondaryMajor && isMajorInMajorsList( secondaryMajor.id, majorsListB ) ) {
                selectableMajors.model[ ITEMS.SELECTOR_PRIMARY ] = [];

                majorsAll.forEach( function( majorToCompare ){
                    if ( isMajorInMajorsList( majorToCompare.id, majorsListA ) ) {
                        selectableMajors.model[ ITEMS.SELECTOR_PRIMARY ].push( majorToCompare );
                    }
                });
            }
        }


        /**
         * @param {string} majorId
         * @param {Object[]} majorsListToSearch
         *
         * @returns {boolean}
         */
        function isMajorInMajorsList( majorId, majorsListToSearch ) {
            for ( var i = 0; i < majorsListToSearch.length; i++ ){
                if ( majorsListToSearch[ i ].asset_metadata_WebPath === majorId ) return true;
            };
            return false;
        }


        /**
         * If the item with given `itemId` is specified in URL's `primaryItem`
         * or `secondaryItem` queries, mark it as a 'primary' OR a 'secondary'
         * item in its {SelectablesConfig}'s type attribute.
         *
         * Otherwise, mark it as an 'other' item.
         *
         * @param {string} itemId
         * @param {Object} itemObject
         * @param {Object} queryParamsValues
         */
        function assignTypeFromCurrentQueries ( itemId, itemObject, queryParamsValues ) {
            if ( itemId === queryParamsValues[ ITEMS.PRIMARY ]) {
                itemObject.type = ITEMS.PRIMARY;
            } else if ( itemId === queryParamsValues[ ITEMS.SECONDARY ]) {
                itemObject.type = ITEMS.SECONDARY;
            } else {
                itemObject.type = ITEMS.OTHERS;
            }
        }


        /**
         * Updates items' types and re-sorts them into correct item groups.
         * Finally, updates URL to match the selected state.
         *
         * @param {Selectables} selectableObject
         */
        function updateSelectableStructure( selectableObject ) {
            var urlQueryUpdateObject = {},
            
            selectableModel     = selectableObject.model,
            selectableConfig    = selectableObject.config;

            logger.warn ("In here");
            logger.warn (JSON.stringify(selectableModel));
            logger.warn ("***");
            logger.warn (JSON.stringify(selectableConfig));
            logger.warn ("***");
            logger.warn (JSON.stringify(ITEMS.PRIMARY));    // If this fails (i.e. use jsonnot JSON) selector works  )

            // if (selectableConfig.type === "specialisation") return;

            // Primary item
            if ( selectableModel[ ITEMS.PRIMARY ] !== selectableModel[ ITEMS.PREV_PRIMARY ]) {
                if ( selectableModel[ ITEMS.PREV_PRIMARY ] ) selectableModel[ ITEMS.PREV_PRIMARY ].type = ITEMS.OTHERS;

                if ( selectableModel[ ITEMS.PRIMARY ] ) {
                    selectableModel[ ITEMS.PRIMARY ].type = ITEMS.PRIMARY;
                    urlQueryUpdateObject[ selectableConfig.queryParamsId[ ITEMS.PRIMARY ] ] = selectableModel[ ITEMS.PRIMARY ].id;
                    selectableConfig.queryParamsValues[ ITEMS.PRIMARY ] = selectableModel[ ITEMS.PRIMARY ].id;
                } else {
                    urlQueryUpdateObject[ selectableConfig.queryParamsId[ ITEMS.PRIMARY ] ] = null;
                    selectableConfig.queryParamsValues[ ITEMS.PRIMARY ] = null;
                }

                selectableModel[ ITEMS.PREV_PRIMARY ] = selectableModel[ ITEMS.PRIMARY ];
            }

            // Secondary item
            if ( selectableModel[ ITEMS.SECONDARY ] !== selectableModel[ ITEMS.PREV_SECONDARY ]) {
                if ( selectableModel[ ITEMS.PREV_SECONDARY ] && selectableModel[ ITEMS.PREV_SECONDARY ] !== selectableModel[ ITEMS.PRIMARY ] ) selectableModel[ ITEMS.PREV_SECONDARY ].type = ITEMS.OTHERS;

                if ( selectableModel[ ITEMS.SECONDARY ] ) {
                    selectableModel[ ITEMS.SECONDARY ].type = ITEMS.SECONDARY;

                    urlQueryUpdateObject[ selectableConfig.queryParamsId[ ITEMS.SECONDARY ] ] = selectableModel[ ITEMS.SECONDARY ].id;
                    selectableConfig.queryParamsValues[ ITEMS.SECONDARY ] = selectableModel[ ITEMS.SECONDARY ].id;
                } else {
                    urlQueryUpdateObject[ selectableConfig.queryParamsId[ ITEMS.SECONDARY ] ] = null;
                    selectableConfig.queryParamsValues[ ITEMS.SECONDARY ] = null;
                }

                selectableModel[ ITEMS.PREV_SECONDARY ] = selectableModel[ ITEMS.SECONDARY ];
            }

            divideItemsToModelGroups( selectableObject );

            // Maintain the anchor
            urlQueryUpdateObject[ '#' ] = $uiRouterGlobals.params[ '#' ];

            $state.go( '.', urlQueryUpdateObject, { location: 'replace', notify: false, reload: false, inherit: true } );
        }


        /**
         * Divides items from 'all' into 'primary', 'secondary'
         * and 'others' (~ the rest) items.
         */
        function divideItemsToModelGroups ( selectableObject ) {
            var selectableModel = selectableObject.model,

            modelOthers     = selectableModel[ ITEMS.OTHERS ],
            modelAll        = selectableModel[ ITEMS.ALL ];

            modelOthers.length = 0; // Reset 'others' group

            modelAll.forEach( function( item ) {
                if ( item.type === ITEMS.PRIMARY ){
                    selectableModel[ ITEMS.PRIMARY ] = item;
                    selectableModel[ ITEMS.PREV_PRIMARY ] = item;
                } else if ( item.type === ITEMS.SECONDARY ) {
                    selectableModel[ ITEMS.SECONDARY ] = item;
                    selectableModel[ ITEMS.PREV_SECONDARY ] = item;
                } else {
                    modelOthers.push( item );
                }
            });
            
        }


        function resolveSelectablesPromiseIfExists( selectablesName ) {
            if ( selectablesDefers.hasOwnProperty( selectablesName ) ) {
                selectablesDefers[ selectablesName ].resolve( selectables[ selectablesName ]);
            }
        }

        function removeSelectablesPromiseIfExists( selectablesName ) {
            if (selectablesDefers.hasOwnProperty(selectablesName) && selectablesDefers[selectablesName].promise.$$state.status === 1 ) {
                delete selectablesDefers[selectablesName];
            }
        }
    }
})();
